/**
 * Permite detener las cintas animadas del tema.
 *
 * El criterio 2.2.2 de las WCAG dice que todo movimiento automático que dure
 * más de cinco segundos debe poder pararse. Estas cintas no paraban nunca: el
 * tema solo ofrece «pausar al pasar el ratón», que ni es teclado ni sirve en
 * un móvil, y ni siquiera estaba activado.
 *
 * Por qué en un fichero aparte y no tocando `theme.js`: la animación vive en
 * `MarqueeText`, dentro del bundle del tema, y su referencia está en un campo
 * privado (`WeakMap`) al que no se llega desde fuera. Editar ese fichero
 * significaría perder el cambio en la próxima actualización de Prestige. En
 * lugar de eso se usa la API de animaciones del navegador: la animación está
 * aplicada al elemento `[part="scroller"]` del shadow DOM, así que
 * `getAnimations()` la devuelve y se puede pausar y reanudar desde aquí.
 *
 * Ojo, esto NO se puede resolver con CSS: la cinta no usa `@keyframes`, la
 * mueve la Web Animations API, y un `animation: none` no la toca.
 */

(() => {
    const REDUCIR = window.matchMedia('(prefers-reduced-motion: reduce)');

    /** Las animaciones del scroller, que es quien se mueve de verdad. */
    function animaciones(cinta) {
        const scroller = cinta.shadowRoot?.querySelector('[part="scroller"]');
        return scroller ? scroller.getAnimations() : [];
    }

    function pausar(cinta) {
        animaciones(cinta).forEach((a) => a.pause());
        cinta.dataset.pausada = 'true';
    }

    function reanudar(cinta) {
        if (REDUCIR.matches) return; // quien pidió no ver movimiento, no lo ve
        animaciones(cinta).forEach((a) => a.play());
        delete cinta.dataset.pausada;
    }

    function preparar(cinta) {
        if (cinta.dataset.a11y === 'listo') return;
        cinta.dataset.a11y = 'listo';

        // El botón se crea aquí y no en el Liquid porque solo tiene sentido si
        // el JavaScript funciona: un botón de pausa que no pausa es peor que
        // no tenerlo.
        const boton = document.createElement('button');
        boton.type = 'button';
        boton.className = 'marquee-pause';
        boton.textContent = cinta.dataset.textoPausar || 'Pausar';
        boton.setAttribute('aria-pressed', 'false');

        boton.addEventListener('click', () => {
            const pausada = cinta.dataset.pausada === 'true';
            if (pausada) {
                reanudar(cinta);
            } else {
                pausar(cinta);
            }
            boton.setAttribute('aria-pressed', String(!pausada));
            boton.textContent = !pausada
                ? cinta.dataset.textoReanudar || 'Reanudar'
                : cinta.dataset.textoPausar || 'Pausar';
        });

        cinta.insertAdjacentElement('afterend', boton);

        // Al llegar con el tabulador a cualquier enlace de dentro, la cinta se
        // para sola: si no, el foco se va moviendo por la pantalla y es
        // imposible leer dónde estás.
        cinta.addEventListener('focusin', () => pausar(cinta));

        if (REDUCIR.matches) pausar(cinta);
    }

    function repasar() {
        document.querySelectorAll('marquee-text').forEach((cinta) => {
            // La animación se crea cuando la cinta entra en pantalla, no al
            // cargar, así que puede no existir todavía. Se prepara igualmente:
            // `pausar` vuelve a consultar `getAnimations()` cada vez.
            preparar(cinta);
            if (REDUCIR.matches) pausar(cinta);
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', repasar);
    } else {
        repasar();
    }

    // Al entrar en pantalla la animación ya existe: se vuelve a pasar para
    // pausarla si hacía falta.
    window.addEventListener('load', repasar);

    REDUCIR.addEventListener('change', () => {
        document.querySelectorAll('marquee-text').forEach((cinta) => {
            if (REDUCIR.matches) {
                pausar(cinta);
            } else if (cinta.dataset.pausada === 'true') {
                reanudar(cinta);
            }
        });
    });

    // El editor de temas monta y desmonta secciones sin recargar la página.
    document.addEventListener('shopify:section:load', repasar);
})();
